import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  BarController,
  LineController,
  Title as ChartTitle,
  Tooltip,
  Legend
} from 'chart.js'
import { MatrixController, MatrixElement } from 'chartjs-chart-matrix'
import dayjs from 'dayjs'

// Registrar componentes do Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  BarController,
  LineController,
  ChartTitle,
  Tooltip,
  Legend
)
ChartJS.register(MatrixController, MatrixElement)

/**
 * Converte um gráfico Chart.js em imagem base64
 * @param {Object} chartConfig - Configuração do gráfico (data e options)
 * @param {number} width - Largura da imagem (padrío: 800)
 * @param {number} height - Altura da imagem (padrío: 400)
 * @returns {Promise<string>} - Promise que resolve com a imagem em base64
 */
export const chartToBase64 = (chartConfig, width = 800, height = 400) => {
  return new Promise((resolve, reject) => {
    try {
      // Criar canvas temporário
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height

      // Configurar contexto para melhor qualidade
      const ctx = canvas.getContext('2d')
      ctx.imageSmoothingEnabled = true
      ctx.imageSmoothingQuality = 'high'

      // Criar instância do Chart.js
      const chart = new ChartJS(ctx, {
        type: chartConfig.type || 'bar',
        data: chartConfig.data,
        options: {
          ...chartConfig.options,
          responsive: false,
          maintainAspectRatio: false,
          animation: false, // Desabilitar animações para captura
          plugins: {
            ...chartConfig.options?.plugins,
            legend: {
              ...chartConfig.options?.plugins?.legend,
              labels: {
                ...chartConfig.options?.plugins?.legend?.labels,
                usePointStyle: true,
                padding: 15
              }
            }
          }
        }
      })

      // Aguardar renderização e converter para base64
      setTimeout(() => {
        try {
          const base64Image = canvas.toDataURL('image/png', 1.0)
          chart.destroy() // Limpar recursos
          resolve(base64Image)
        } catch (error) {
          chart.destroy()
          reject(error)
        }
      }, 100)

    } catch (error) {
      reject(error)
    }
  })
}

/**
 * Gera gráfico de barras para Top 10
 * @param {Array} data - Dados do top 10
 * @param {string} title - Título do gráfico
 * @param {string} theme - Tema (light/dark)
 * @param {string} color - Cor principal
 * @returns {Object} - Configuração do gráfico
 */
export const generateTopChartConfig = (data, title, theme = 'light', color = '#1890ff') => {
  const labels = data.map(item => item.label || 'N/A')
  const values = data.map(item => item.etiquetasImp || 0)

  return {
    type: 'bar',
    data: {
      labels,
      datasets: [{
        label: 'Etiquetas',
        data: values,
        backgroundColor: theme === 'dark' ? 'rgba(24, 144, 255, 0.6)' : 'rgba(24, 144, 255, 0.8)',
        borderColor: color,
        borderWidth: 1
      }]
    },
    options: {
      indexAxis: 'y',
      responsive: false,
      maintainAspectRatio: false,
      plugins: {
        title: {
          display: true,
          text: title,
          font: { size: 16, weight: 'bold' },
          color: theme === 'dark' ? '#ffffff' : '#000000'
        },
        legend: {
          display: false
        }
      },
      scales: {
        x: {
          ticks: {
            color: theme === 'dark' ? '#e5e7eb' : '#111827'
          },
          grid: {
            color: theme === 'dark' ? '#374151' : '#e5e7eb'
          }
        },
        y: {
          ticks: {
            color: theme === 'dark' ? '#e5e7eb' : '#111827'
          },
          grid: {
            color: theme === 'dark' ? '#374151' : '#e5e7eb'
          }
        }
      }
    }
  }
}

/**
 * Função para calcular média móvel
 * @param {Array} data - Array de números
 * @param {number} window - Janela da média móvel
 * @returns {Array} - Array com médias móveis
 */
const movingAvg = (data, window) => {
  const result = []
  for (let i = 0; i < data.length; i++) {
    if (i < window - 1) {
      result.push(null)
    } else {
      const sum = data.slice(i - window + 1, i + 1).reduce((a, b) => a + b, 0)
      result.push(sum / window)
    }
  }
  return result
}

/**
 * Gera gráfico misto (barras + linha) para volume diário
 * @param {Array} dailyData - Dados diários
 * @param {string} theme - Tema (light/dark)
 * @returns {Object} - Configuração do gráfico
 */
export const generateDailyChartConfig = (dailyData, theme = 'light') => {
  // Usar dayjs para formatar as datas como no original

  const labels = dailyData.map(d => dayjs(d.day).format('DD/MM'))
  const etiquetasDiaTotal = dailyData.map(d => (d.etiquetasImp + d.etiquetasSem))
  const ma7 = movingAvg(etiquetasDiaTotal, 7)

  return {
    type: 'bar',
    data: {
      labels,
      datasets: [
        {
          type: 'bar',
          label: 'Etiquetas/dia (Total)',
          data: etiquetasDiaTotal,
          backgroundColor: theme === 'dark' ? 'rgba(59,130,246,0.6)' : 'rgba(59,130,246,0.6)',
          borderColor: theme === 'dark' ? '#93c5fd' : '#3b82f6'
        },
        {
          type: 'line',
          label: 'Média móvel Etiquetas (7d)',
          yAxisID: 'y1',
          data: ma7,
          borderColor: theme === 'dark' ? '#10b981' : '#22c55e',
          backgroundColor: theme === 'dark' ? '#10b981' : '#22c55e',
          tension: 0.3,
          pointRadius: 2
        }
      ]
    },
    options: {
      responsive: false,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            color: theme === 'dark' ? '#e5e7eb' : '#111827'
          }
        },
        title: {
          display: true,
          text: 'Volume Diário + Média Móvel',
          font: { size: 16, weight: 'bold' },
          color: theme === 'dark' ? '#ffffff' : '#000000'
        }
      },
      scales: {
        x: {
          ticks: {
            color: theme === 'dark' ? '#e5e7eb' : '#111827',
            maxRotation: 45
          },
          grid: {
            color: theme === 'dark' ? '#374151' : '#e5e7eb'
          }
        },
        y: {
          ticks: {
            color: theme === 'dark' ? '#e5e7eb' : '#111827'
          },
          grid: {
            color: theme === 'dark' ? '#374151' : '#e5e7eb'
          }
        },
        y1: {
          position: 'right',
          grid: { drawOnChartArea: false },
          ticks: {
            color: theme === 'dark' ? '#e5e7eb' : '#111827'
          }
        }
      }
    }
  }
}

/**
 * Gera gráfico de barras empilhadas
 * @param {Array} dailyData - Dados diários
 * @param {string} theme - Tema (light/dark)
 * @returns {Object} - Configuração do gráfico
 */
export const generateStackedChartConfig = (dailyData, theme = 'light') => {
  // Usar dayjs para formatar as datas como no original

  const labels = dailyData.map(d => dayjs(d.day).format('DD/MM'))
  const impressas = dailyData.map(d => d.etiquetasImp)
  const pendentes = dailyData.map(d => d.etiquetasSem)

  return {
    type: 'bar',
    data: {
      labels,
      datasets: [
        {
          type: 'bar',
          label: 'Impressas',
          data: impressas,
          backgroundColor: theme === 'dark' ? 'rgba(34,197,94,0.7)' : 'rgba(16,185,129,0.7)'
        },
        {
          type: 'bar',
          label: 'Pendentes',
          data: pendentes,
          backgroundColor: theme === 'dark' ? 'rgba(245,158,11,0.7)' : 'rgba(245,158,11,0.7)'
        }
      ]
    },
    options: {
      responsive: false,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            color: theme === 'dark' ? '#e5e7eb' : '#111827'
          }
        },
        title: {
          display: true,
          text: 'Impressas vs Pendentes por Dia',
          font: { size: 16, weight: 'bold' },
          color: theme === 'dark' ? '#ffffff' : '#000000'
        }
      },
      scales: {
        x: {
          stacked: true,
          ticks: {
            color: theme === 'dark' ? '#e5e7eb' : '#111827',
            maxRotation: 45
          },
          grid: {
            color: theme === 'dark' ? '#374151' : '#e5e7eb'
          }
        },
        y: {
          stacked: true,
          ticks: {
            color: theme === 'dark' ? '#e5e7eb' : '#111827'
          },
          grid: {
            color: theme === 'dark' ? '#374151' : '#e5e7eb'
          }
        }
      }
    }
  }
}

/**
 * Gera gráfico de Etiquetas por Filial
 * @param {Array} data - Dados das filiais
 * @param {string} theme - Tema (light/dark)
 * @returns {Object} - Configuração do gráfico
 */
export const generateFiliaisChartConfig = (data, theme = 'light') => {
  return {
    type: 'bar',
    data: {
      labels: data.map(f => f.filial),
      datasets: [{
        label: 'Total Etiquetas',
        data: data.map(f => f.total),
        backgroundColor: '#ff8c00',
        borderColor: '#ff8c00',
        borderWidth: 1,
        borderRadius: 4
      }]
    },
    options: {
      responsive: false,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        title: {
          display: true,
          text: 'Etiquetas por Filial',
          font: { size: 16, weight: 'bold' },
          color: theme === 'dark' ? '#ffffff' : '#000000'
        }
      },
      scales: {
        x: {
          ticks: {
            color: theme === 'dark' ? '#e5e7eb' : '#111827',
            maxRotation: 45
          },
          grid: {
            color: theme === 'dark' ? '#374151' : '#e5e7eb'
          }
        },
        y: {
          ticks: { color: theme === 'dark' ? '#e5e7eb' : '#111827' },
          grid: {
            color: theme === 'dark' ? '#374151' : '#e5e7eb'
          }
        }
      }
    }
  }
}

/**
 * Gera gráfico de Top 1 Carro por Filial
 * @param {Array} data - Dados dos carros por filial
 * @param {string} theme - Tema (light/dark)
 * @returns {Object} - Configuração do gráfico
 */
export const generateCarrosPorFilialChartConfig = (data, theme = 'light') => {
  return {
    type: 'bar',
    data: {
      labels: data.map(c => `${c.filial} - ${c.carro}`),
      datasets: [{
        label: 'Total Etiquetas (Top 1 por Filial)',
        data: data.map(c => c.total),
        backgroundColor: '#1890ff',
        borderColor: '#1890ff',
        borderWidth: 1,
        borderRadius: 4
      }]
    },
    options: {
      indexAxis: 'y',
      responsive: false,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        title: {
          display: true,
          text: 'Top 1 Carro por Filial (Etiquetas)',
          font: { size: 16, weight: 'bold' },
          color: theme === 'dark' ? '#ffffff' : '#000000'
        }
      },
      scales: {
        x: {
          ticks: { color: theme === 'dark' ? '#e5e7eb' : '#111827' },
          grid: {
            color: theme === 'dark' ? '#374151' : '#e5e7eb'
          }
        },
        y: {
          ticks: {
            color: theme === 'dark' ? '#e5e7eb' : '#111827',
            font: { size: 10 }
          },
          grid: {
            color: theme === 'dark' ? '#374151' : '#e5e7eb'
          }
        }
      }
    }
  }
}

/**
 * Helper para cores do heatmap
 */
const heatColor = (v, max, theme) => {
  const t = max ? Math.max(0, Math.min(1, v / max)) : 0
  const stops = [
    [255, 247, 194], [253, 219, 126], [245, 178, 85], [239, 108, 57], [230, 69, 44], [184, 30, 30]
  ]
  const n = stops.length - 1
  const i = Math.floor(t * n)
  const frac = t * n - i
  const c1 = stops[i]
  const c2 = stops[Math.min(i + 1, n)]
  const r = Math.round(c1[0] + (c2[0] - c1[0]) * frac)
  const g = Math.round(c1[1] + (c2[1] - c1[1]) * frac)
  const b = Math.round(c1[2] + (c2[2] - c1[2]) * frac)
  return `rgba(${r}, ${g}, ${b}, ${theme === 'dark' ? 0.9 : 0.85})`
}

/**
 * Gera gráfico Heatmap
 * @param {Object} heatmapData - Dados do heatmap (labelsX, labelsY, map, max)
 * @param {string} theme - Tema (light/dark)
 * @returns {Object} - Configuração do gráfico
 */
export const generateHeatmapChartConfig = (heatmapData, theme = 'light') => {
  const { labelsX, labelsY, map, max } = heatmapData;
  const dataPoints = [];
  for (const day of labelsX) {
    for (let h = 0; h < 24; h++) {
      const v = map.get(`${day}|${h}`) || 0;
      dataPoints.push({ x: day, y: `${String(h).padStart(2, '0')}h`, v });
    }
  }

  return {
    type: 'matrix',
    data: {
      datasets: [{
        label: 'Etiquetas por Dia/Hora',
        data: dataPoints,
        backgroundColor: (ctx) => {
          const v = ctx.dataset.data[ctx.dataIndex]?.v || 0;
          return heatColor(v, max, theme);
        },
        borderWidth: 1,
        width: ({ chart }) => {
          const area = chart.chartArea;
          return area ? (area.width / (labelsX.length || 1)) - 1 : 20;
        },
        height: ({ chart }) => {
          const area = chart.chartArea;
          return area ? (area.height / (labelsY.length || 1)) - 1 : 20;
        }
      }]
    },
    options: {
      responsive: false,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        title: {
          display: true,
          text: 'Heatmap de Horários (Dia x Hora)',
          font: { size: 16, weight: 'bold' },
          color: theme === 'dark' ? '#ffffff' : '#000000'
        },
        tooltip: {
          callbacks: {
            title: (items) => items?.[0]?.raw ? `${items[0].raw.x} - ${items[0].raw.y}` : '',
            label: (item) => `Etiquetas: ${item.raw?.v ?? 0}`
          }
        }
      },
      scales: {
        x: {
          type: 'category',
          labels: labelsX,
          ticks: { color: theme === 'dark' ? '#e5e7eb' : '#111827' },
          grid: { display: false }
        },
        y: {
          type: 'category',
          labels: labelsY,
          ticks: {
            color: theme === 'dark' ? '#e5e7eb' : '#111827',
            font: { size: 9 }
          },
          grid: { display: false }
        }
      }
    }
  }
}